import React from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, Text, ConfirmationModal, currency } from '@frontend/design-system';
import { useScheduleRequestItemHeaderController } from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestItemHeaderUI } from '../components/schedule-request-item-header-ui';
import { ScheduleRequestDeleteConfirmationModal } from '../components/ScheduleRequestDeleteConfirmationModal';

interface Props {
  requestVersion: string;
  name: string;
  personId?: string;
  appointmentTime: string;
  personPhone: string;
  paidAmount: number;
  locationId: string;
  scheduleRequestId: string;
  isWidgetListItem?: boolean;
  locationChip?: (locationName: string) => React.ReactNode;
  markNotificationsAsRead: (val: string) => void;
  updateSelectedScheduleRequest: () => void;
}

export const ScheduleRequestListItemHeader = React.memo(
  ({
    requestVersion,
    name,
    paidAmount,
    personId,
    appointmentTime,
    personPhone,
    locationId,
    scheduleRequestId,
    isWidgetListItem,
    updateSelectedScheduleRequest,
    markNotificationsAsRead,
    locationChip,
  }: Props) => {
    const { t } = useTranslation('schedule-requests');

    const {
      shouldUseV3,
      locationName,
      canSendMessages,
      appointmentTimeLabel,
      isDeleting,
      isUpdating,
      deleteScheduleRequestModalProps,
      rejectScheduleRequestModalControl,
      getMenuProps,
      getTriggerProps,
      handleReviewRequestMenuItemClick,
      handleRejectRequestMenuItemClick,
      handleDeleteMenuItemClick,
      handleMessageMenuItemClick,
      handleDeleteScheduleRequest,
      handleRejectScheduleRequest,
    } = useScheduleRequestItemHeaderController({
      requestVersion,
      locationId,
      scheduleRequestId,
      appointmentTime,
      isWidgetListItem,
      markNotificationsAsRead,
    });

    const secondaryMeta = (
      <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
        {!!paidAmount ? (
          <>
            <Text textAlign='left' size='small' css={{ display: 'inline', color: theme.colors.tangerine50 }}>
              {currency(paidAmount, { decimals: 2 })}
            </Text>
            {` • `}
          </>
        ) : null}
        <Text textAlign='left' size='small'>
          {appointmentTimeLabel}
        </Text>
      </div>
    );

    const menuItems = (
      <>
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemReviewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={() => handleReviewRequestMenuItemClick(updateSelectedScheduleRequest)}
        >
          {t('Review Request')}
        </PopoverMenuItem>
        {personPhone && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
            Icon={() => <Icon name='message-small' />}
            css={{ width: '100%' }}
            disabled={!canSendMessages}
            hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
            onClick={async () => {
              await handleMessageMenuItemClick({
                personId,
                personPhone,
                threadGroupId: locationId,
                groupIds: [locationId],
              });
            }}
          >
            {t('Message')}
          </PopoverMenuItem>
        )}
        {shouldUseV3 && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemRejectBtn}
            Icon={() => <Icon name='calendar-discarded-small' />}
            onClick={handleRejectRequestMenuItemClick}
          >
            {t('Reject Request')}
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
          Icon={() => <Icon name='trash-small' />}
          onClick={handleDeleteMenuItemClick}
        >
          {t('Delete')}
        </PopoverMenuItem>
      </>
    );

    const modals = (
      <>
        <ScheduleRequestDeleteConfirmationModal
          onConfirm={handleDeleteScheduleRequest}
          modalProps={deleteScheduleRequestModalProps.modalProps}
        />
        <ConfirmationModal
          destructive
          {...rejectScheduleRequestModalControl.modalProps}
          title={t('Confirm Reject')}
          message={t('Are you sure you want to reject this request?')}
          onConfirm={handleRejectScheduleRequest}
          cancelTrackingId={ScheduleRequestsTrackingIds.rejectConfirmationModalCancelBtn}
          confirmTrackingId={ScheduleRequestsTrackingIds.rejectConfirmationModalConfirmBtn}
        />
      </>
    );

    return (
      <ScheduleRequestItemHeaderUI
        name={name}
        isLoading={isDeleting || isUpdating}
        locationName={locationName}
        secondaryMeta={secondaryMeta}
        menuItems={menuItems}
        modals={modals}
        locationChip={locationChip}
        getTriggerProps={getTriggerProps}
        getMenuProps={getMenuProps}
      />
    );
  }
);

ScheduleRequestListItemHeader.displayName = 'ScheduleRequestListItemHeader';
